"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { format } from "date-fns";
import {
  ArrowLeftRightIcon,
  ArrowLeftIcon,
  CarIcon,
  CircleCheckBigIcon,
  CreditCardIcon,
  DownloadIcon,
  FileTextIcon,
  SendIcon,
  UserIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { syncMdmFromDraft } from "@/app/lib/actions/cat/mdm-sync";
import { transmitRetailCtmAction } from "@/app/lib/actions/cat/ctm-transmit";
import {
  RetailCtmTransmissionTaskList,
  type TransmissionTaskItem,
  type TransmissionTaskStatus,
} from "@/components/backoffice/cat/vehicle-assignment/summary-validation-step/retail/retail-ctm-transmission-task-list";
import { useVehicleAssignmentStepError } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";
import { determineSaleType } from "@/app/lib/actions/cat/sale-type";
import { FlexRow } from "@/components/flex-row";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useStep } from "@/hooks/use-step-handler";
import type { SearchVehicleDataResult } from "@/lib/cat/mulesoft-vehicle-search";
import {
  buildCompanyRecapFromDraft,
  buildIndividualRecapFromDraft,
  getClientRecapTypeLabel,
  getIndividualRecapSectionTitle,
  shouldShowClientRecap,
  shouldShowCompanyRecap,
  shouldShowIndividualRecap,
} from "@/lib/cat/mdm-recap-from-draft";
import { getVehicleOptionPrNumber } from "@/lib/cat/vehicle-option-pr-number";
import {
  getLemaSalesPersonsQueryParamsFromKvpsIdentifier,
  isLemaSalesPersonsFilterActive,
} from "@/lib/cat/lema-sales-persons-query-params";
import { mapDraftToTradeInFormState } from "@/lib/schemas/cat/vehicle-trade-in-save-draft";
import { cn } from "@/lib/utils";
import { resolveBodyShopLabelFromKvps } from "@/lib/cat/resolve-body-shop-label";
import { useLemaSalesPersonsQuery, useVehicleBrandsQuery } from "@/lib/queries";
import { useBodyTypesQuery } from "@/lib/queries/body-types";
import type { Draft, DraftTradeInVehicle } from "@/prisma/generated/client";
import type { MdmSyncOutcome } from "@/types/cat/mdm-party-resolution.types";
import {
  downloadRetailCtmSummaryPdf,
  formatRetailCtmPdfDate,
  formatRetailCtmPdfTradeIn,
  formatRetailCtmPdfYesNo,
  toPdfDisplayValue,
  type RetailCtmSummaryPdfData,
} from "@/lib/cat/retail-ctm-summary-pdf";
import {
  kvpsBrandLetterToDisplayLabel,
  resolveKvpsDisplayLabel,
  type Kvps,
} from "@/lib/kvps";
import { formatProductionWeekLabel } from "@/lib/cat/format-production-week";
import { getVwfsLessorSiret } from "@/lib/cat/vwfs-lessor-siret";
import { resetVehicleAssignmentStores } from "@/lib/cat/reset-vehicle-assignment-stores";
import { toast } from "sonner";
import { useMdmClientHealthStore } from "@/stores/mdm-client-health-store";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { useRetailVehicleAssignmentStore } from "@/stores/mulesoft-vehicle-data-Store";
type DraftWithTradeIn = Draft & {
  tradeInVehicles?: DraftTradeInVehicle[];
};

export interface RetailSummaryValidationStepProps {
  currentKvps?: Kvps | null;
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
  onGoToPreviousStep?: (targetStep: number) => void;
}

const EMPTY_VALUE = "";

type RefPersTransmissionState = {
  status: TransmissionTaskStatus;
  mdmOutcome?: MdmSyncOutcome;
  message?: string;
};

type CtmTransmissionState = {
  status: TransmissionTaskStatus;
  message?: string;
};

function derivePreviewCtmTransmission(
  hasActiveDraft: boolean,
): CtmTransmissionState {
  if (!hasActiveDraft) {
    return { status: "pending" };
  }
  return { status: "pending" };
}

function buildCtmTransmissionDescription(state: CtmTransmissionState): string {
  switch (state.status) {
    case "pending":
      return "En attente de synchronisation RefPers";
    case "in_progress":
      return "Transmission en cours…";
    case "success":
      return "Transmise avec succès";
    case "failed":
      return state.message ?? "Transmission bloquée";
    case "skipped":
      return "Non applicable";
  }
}

function derivePreviewRefPersTransmission(
  hasActiveDraft: boolean,
  showIndividualRecap: boolean,
  showCompanyRecap: boolean,
  healthMessage: string | null,
): RefPersTransmissionState {
  if (!hasActiveDraft) {
    return { status: "pending" };
  }

  if (!showIndividualRecap && !showCompanyRecap) {
    return { status: "skipped", mdmOutcome: "not_applicable" };
  }
  if (healthMessage) {
    return { status: "failed", message: healthMessage };
  }
  return { status: "pending" };
}

function buildRefPersTransmissionDescription(
  state: RefPersTransmissionState,
): string {
  switch (state.status) {
    case "pending":
      return "Pas encore transmise";
    case "in_progress":
      return "Transmission en cours…";
    case "success":
      return "Transmise avec succès";
    case "skipped":
      return state.mdmOutcome === "not_applicable"
        ? "Non applicable pour ce dossier"
        : "Aucune mise à jour nécessaire";
    case "failed":
      return state.message ?? "Transmission bloquée";
  }
}

function formatDraftDate(value: Date | string | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return format(date, "yyyy-MM-dd");
}

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY_VALUE;
}

function resolveTrimmedValue(
  ...candidates: (string | null | undefined)[]
): string {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return "";
}

function RecapFieldsList({
  fields,
}: {
  fields: Array<{ label: string; value: string }>;
}) {
  if (fields.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">Aucune donnée client.</p>
    );
  }
  return (
    <div className="space-y-2">
      {fields.map((field) => (
        <SummaryFieldRow key={field.label} label={field.label}>
          <span className="wrap-break-words block max-w-[280px] text-right">
            {field.value}
          </span>
        </SummaryFieldRow>
      ))}
    </div>
  );
}

function formatVehicleOptions(vehicle: SearchVehicleDataResult | null): string {
  if (!vehicle?.options?.length) return EMPTY_VALUE;

  const optionNumbers = vehicle.options
    .map((option) => getVehicleOptionPrNumber(option))
    .filter(Boolean);

  return optionNumbers.length > 0 ? optionNumbers.join(", ") : EMPTY_VALUE;
}

function formatVehicleOptionsForPdf(
  vehicle: SearchVehicleDataResult | null,
): string {
  const options = formatVehicleOptions(vehicle);
  if (options === EMPTY_VALUE) return options;
  return options.replace(/, /g, " + ");
}

function resolveLeasingTypeLabel(draft: Draft | null): string {
  if (!draft?.isLeased) return EMPTY_VALUE;
  if (draft.fundingType === "VWFS" && draft.vwfsType) {
    return draft.vwfsType === "LOUN"
      ? "LOUN VWFS"
      : draft.vwfsType.replace(/_/g, " ");
  }
  if (draft.fundingType === "OTHER" && draft.otherLeasingType) {
    return draft.otherLeasingType;
  }
  return EMPTY_VALUE;
}

function formatBooleanYesNo(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return value ? "Oui" : "Non";
}

function resolveLessorSiret(draft: Draft | null): string {
  const stored = draft?.lessorSiret?.trim();
  if (stored) return stored;

  if (draft?.fundingType === "VWFS" && draft.vwfsType) {
    return getVwfsLessorSiret(draft.vwfsType);
  }

  return EMPTY_VALUE;
}

function SummaryFieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <div className="min-w-0 text-right font-medium">{children}</div>
    </div>
  );
}

function SummaryInfoCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Icon className="text-primary size-5 shrink-0" aria-hidden />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

export function RetailSummaryValidationStep({
  currentKvps,
  onBeforeNextChange,
  onCanProceedChange,
  onGoToPreviousStep,
}: RetailSummaryValidationStepProps) {
  const router = useRouter();
  const { step } = useStep();
  const { showStepError } = useVehicleAssignmentStepError();
  const mdmClientHealthMessage = useMdmClientHealthStore(
    (state) => state.message,
  );
  const currentDraft = useCurrentDraftStore(
    (state) => state.currentDraft,
  ) as DraftWithTradeIn | null;
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const muleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.muleSoftVehicleData,
  );

  const [saleTypeCache, setSaleTypeCache] = useState<{
    draftId: number;
    code: string;
  } | null>(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSubmittingCtm, setIsSubmittingCtm] = useState(false);
  const [refPersTransmission, setRefPersTransmission] =
    useState<RefPersTransmissionState>(() =>
      derivePreviewRefPersTransmission(
        Boolean(currentDraft),
        shouldShowIndividualRecap(currentDraft),
        shouldShowCompanyRecap(currentDraft),
        null,
      ),
    );
  const [ctmTransmission, setCtmTransmission] = useState<CtmTransmissionState>(
    () => derivePreviewCtmTransmission(Boolean(currentDraft)),
  );
  const [transmissionListKey, setTransmissionListKey] = useState(0);
  const refPersSubmitAttemptedRef = useRef(false);

  const vehicleBrandsQuery = useVehicleBrandsQuery({ sort: { name: "asc" } });
  const bodyTypesQuery = useBodyTypesQuery({ sort: { name: "asc" } });

  const lemaSalesPersonsQueryParams = useMemo(
    () => getLemaSalesPersonsQueryParamsFromKvpsIdentifier(currentDraft?.kvps),
    [currentDraft?.kvps],
  );

  const lemaSalesPersonsQuery = useLemaSalesPersonsQuery(
    lemaSalesPersonsQueryParams,
    undefined,
    {
      enabled: isLemaSalesPersonsFilterActive(lemaSalesPersonsQueryParams),
    },
  );

  const tradeInState = useMemo(
    () => mapDraftToTradeInFormState(currentDraft),
    [currentDraft],
  );

  const brandNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const brand of vehicleBrandsQuery.data?.vehicleBrands ?? []) {
      map.set(brand.id, brand.name);
    }
    return map;
  }, [vehicleBrandsQuery.data?.vehicleBrands]);

  const salesPersonLabel = useMemo(() => {
    if (!currentDraft?.salesPersonId) return EMPTY_VALUE;

    const person = lemaSalesPersonsQuery.data?.lemaSalesPersons.find(
      (row) => row.id === currentDraft.salesPersonId,
    );

    if (!person) return EMPTY_VALUE;

    const first = (person.firstname ?? "").trim();
    const last = (person.lastname ?? "").trim();
    const fullName = `${first} ${last}`.trim();
    return fullName || EMPTY_VALUE;
  }, [currentDraft, lemaSalesPersonsQuery.data?.lemaSalesPersons]);

  const draftId = currentDraft?.id;
  const saleTypeCode =
    draftId === undefined
      ? EMPTY_VALUE
      : saleTypeCache?.draftId === draftId
        ? saleTypeCache.code
        : EMPTY_VALUE;

  const tradeInPdfLines = useMemo(() => {
    if (tradeInState.answer !== "yes") return [];

    return tradeInState.vehicles
      .filter((vehicle) => vehicle.brandId !== null || vehicle.model.trim())
      .map((vehicle, index) => {
        const brandName =
          vehicle.brandId !== null
            ? (brandNameById.get(vehicle.brandId) ?? "")
            : "";
        const model = vehicle.model.trim();
        if (!brandName && !model) return null;
        return `Reprise ${index + 1} : Marque ${brandName} - Modèle : ${model}`;
      })
      .filter((line): line is string => line !== null);
  }, [brandNameById, tradeInState]);

  const tradeInEntries = useMemo(() => {
    if (tradeInState.answer !== "yes") return [];

    return tradeInState.vehicles
      .filter((vehicle) => vehicle.brandId !== null || vehicle.model.trim())
      .map((vehicle, index) => {
        const brandName =
          vehicle.brandId !== null
            ? (brandNameById.get(vehicle.brandId) ?? EMPTY_VALUE)
            : EMPTY_VALUE;
        const model = vehicle.model.trim() || EMPTY_VALUE;
        return {
          key: vehicle.id ?? index,
          label: `Véhicule ${index + 1}`,
          value: `${brandName} ${model}`.trim(),
        };
      });
  }, [brandNameById, tradeInState]);

  const vehicleVin = displayValue(
    muleSoftVehicleData?.vin ?? currentDraft?.vinNumber,
  );
  const vehicleBrand = displayValue(
    kvpsBrandLetterToDisplayLabel(muleSoftVehicleData?.brand),
  );
  const vehicleModelCode = displayValue(muleSoftVehicleData?.modelCode);
  const vehicleModelDescription = displayValue(
    muleSoftVehicleData?.modelDescription,
  );
  const vehicleModelYear = displayValue(muleSoftVehicleData?.modelYear);
  const vehiclePaintCode = displayValue(muleSoftVehicleData?.paintCode);
  const vehiclePaintDescription = displayValue(
    muleSoftVehicleData?.paintDescription,
  );
  const vehicleInteriorCode = displayValue(muleSoftVehicleData?.interiorCode);
  const vehicleInteriorDescription = displayValue(
    muleSoftVehicleData?.interiorDescription,
  );
  const vehicleOptions = formatVehicleOptions(muleSoftVehicleData);
  const vehicleOptionsForPdf = formatVehicleOptionsForPdf(muleSoftVehicleData);
  const vehicleProductionDate =
    formatProductionWeekLabel(muleSoftVehicleData?.productionDate) ??
    displayValue(muleSoftVehicleData?.productionDate);
  const vehicleProductionNumber = resolveTrimmedValue(
    muleSoftVehicleData?.commissionNumber,
    currentDraft?.commissionNumber,
  );
  const vehicleProductionYear = resolveTrimmedValue(
    muleSoftVehicleData?.commissionYear,
    currentDraft?.commissionYear,
  );
  const vehicleCommissionNumber = displayValue(vehicleProductionNumber);
  const vehicleCommissionYear = displayValue(vehicleProductionYear);

  const vehicleKvps =
    currentDraft?.kvps?.trim() ||
    muleSoftVehicleData?.owningDealerKvps?.trim() ||
    EMPTY_VALUE;

  const showClientRecap = shouldShowClientRecap(currentDraft);
  const showCompanyRecap = shouldShowCompanyRecap(currentDraft);
  const showIndividualRecap = shouldShowIndividualRecap(currentDraft);

  useEffect(() => {
    if (
      !mdmClientHealthMessage ||
      (!showIndividualRecap && !showCompanyRecap)
    ) {
      return;
    }
    showStepError(mdmClientHealthMessage);
  }, [
    mdmClientHealthMessage,
    showCompanyRecap,
    showIndividualRecap,
    showStepError,
  ]);

  useEffect(() => {
    refPersSubmitAttemptedRef.current = false;
    setIsSubmittingCtm(false);
    setTransmissionListKey((key) => key + 1);
    setRefPersTransmission(
      derivePreviewRefPersTransmission(
        Boolean(currentDraft),
        showIndividualRecap,
        showCompanyRecap,
        mdmClientHealthMessage,
      ),
    );
    setCtmTransmission(derivePreviewCtmTransmission(Boolean(currentDraft)));
  }, [currentDraft?.id]);

  useEffect(() => {
    if (refPersSubmitAttemptedRef.current || !currentDraft?.id) {
      return;
    }
    setRefPersTransmission(
      derivePreviewRefPersTransmission(
        true,
        showIndividualRecap,
        showCompanyRecap,
        mdmClientHealthMessage,
      ),
    );
  }, [
    currentDraft?.id,
    mdmClientHealthMessage,
    showCompanyRecap,
    showIndividualRecap,
  ]);

  const transmissionTasks = useMemo((): TransmissionTaskItem[] => {
    const refPersStatus =
      isSubmittingCtm && refPersTransmission.status !== "failed"
        ? refPersTransmission.status === "pending"
          ? "in_progress"
          : refPersTransmission.status
        : refPersTransmission.status;

    const ctmStatus =
      isSubmittingCtm &&
      refPersTransmission.status !== "failed" &&
      (refPersStatus === "success" || refPersStatus === "skipped")
        ? ctmTransmission.status === "pending"
          ? "in_progress"
          : ctmTransmission.status
        : ctmTransmission.status;

    return [
      {
        id: "refpers",
        label: "Synchronisation RefPers",
        status: refPersStatus,
        description: buildRefPersTransmissionDescription({
          ...refPersTransmission,
          status: refPersStatus,
        }),
      },
      {
        id: "ctm",
        label: "Transmission CTM",
        status: ctmStatus,
        description: buildCtmTransmissionDescription({
          status: ctmStatus,
        }),
      },
    ];
  }, [ctmTransmission, isSubmittingCtm, refPersTransmission]);

  const isDirectorSale = currentDraft?.directorSale === true;
  const companyRecapFields = useMemo(
    () =>
      buildCompanyRecapFromDraft(currentDraft, {
        directorSale: isDirectorSale,
      }),
    [currentDraft, isDirectorSale],
  );
  const individualRecapFields = useMemo(
    () => buildIndividualRecapFromDraft(currentDraft),
    [currentDraft],
  );
  const clientRecapTypeLabel = getClientRecapTypeLabel(currentDraft);
  const individualRecapSectionTitle =
    getIndividualRecapSectionTitle(currentDraft);
  const directorSaleLabel = formatBooleanYesNo(currentDraft?.directorSale);
  const isVehicleLeased = currentDraft?.isLeased === true;
  const driverFirstName = displayValue(currentDraft?.driverFirstName);
  const driverLastName = displayValue(currentDraft?.driverLastName);
  const lessorSiretLabel = isVehicleLeased
    ? resolveLessorSiret(currentDraft)
    : EMPTY_VALUE;

  const preferredDeliveryLabel = formatDraftDate(
    currentDraft?.preferredDeliveryDate,
  );
  const signatureDateLabel = formatDraftDate(currentDraft?.signatureDate);
  const maxDeliveryDateLabel = formatDraftDate(currentDraft?.maxDeliveryDate);

  const isRentSale = currentDraft?.rentSale === true;
  const leasingTypeLabel = resolveLeasingTypeLabel(currentDraft);
  const isVwfsLeasing = currentDraft?.fundingType === "VWFS";

  const isBodyworkVehicle = currentDraft?.needsBodywork === true;

  const bodyTypeLabel = useMemo(() => {
    if (!isBodyworkVehicle || currentDraft?.bodyTypeId == null) {
      return EMPTY_VALUE;
    }

    const bodyType = bodyTypesQuery.data?.bodyTypes.find(
      (row) => row.id === currentDraft.bodyTypeId,
    );

    return bodyType?.name?.trim() || EMPTY_VALUE;
  }, [
    bodyTypesQuery.data?.bodyTypes,
    currentDraft?.bodyTypeId,
    isBodyworkVehicle,
  ]);

  const bodyShopName = useMemo(() => {
    if (!isBodyworkVehicle) return EMPTY_VALUE;
    return (
      resolveBodyShopLabelFromKvps(currentDraft?.bodyShopKvps)?.trim() ||
      EMPTY_VALUE
    );
  }, [currentDraft?.bodyShopKvps, isBodyworkVehicle]);

  const bodyShopKvpsLabel = displayValue(currentDraft?.bodyShopKvps);

  const shippedToBodyShopLabel = isBodyworkVehicle
    ? formatBooleanYesNo(currentDraft?.shippedToBodyShop)
    : EMPTY_VALUE;

  const tradeInCount = tradeInEntries.length;
  const hasTradeIn = tradeInState.answer === "yes" && tradeInCount > 0;

  useEffect(() => {
    onBeforeNextChange?.(() => true);
    return () => onBeforeNextChange?.(null);
  }, [onBeforeNextChange]);

  useEffect(() => {
    onCanProceedChange?.(true);
  }, [onCanProceedChange]);

  useEffect(() => {
    if (draftId === undefined) return;

    let cancelled = false;

    const run = async () => {
      const result = await determineSaleType(draftId);
      if (cancelled) return;

      const code =
        result.success && result.data?.saleTypeCode
          ? result.data.saleTypeCode
          : EMPTY_VALUE;
      setSaleTypeCache({ draftId, code });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [draftId]);

  const buildPdfData = (): RetailCtmSummaryPdfData => {
    const pdf = toPdfDisplayValue;
    const clientType = isRentSale ? "" : pdf(clientRecapTypeLabel);
    const individualClient = showIndividualRecap;
    const companyClient = showCompanyRecap;
    const kvpsLabel = resolveKvpsDisplayLabel(currentKvps?.label);

    return {
      kvpsIdentifier: pdf(
        currentDraft?.kvps ?? currentKvps?.identifier ?? undefined,
      ),
      kvpsLabel,
      vehicle: {
        brand: pdf(vehicleBrand),
        model: pdf(vehicleModelDescription),
        paint: pdf(vehiclePaintDescription),
        interior: pdf(vehicleInteriorDescription),
        options: pdf(vehicleOptionsForPdf),
        commissionYear: pdf(vehicleCommissionYear),
        orderStatus: "",
        commissionNumber: pdf(vehicleCommissionNumber),
        vin: pdf(vehicleVin),
        isBodyworkVehicle: formatRetailCtmPdfYesNo(currentDraft?.needsBodywork),
        showBodyworkDetails: isBodyworkVehicle,
        bodyTypeLabel: pdf(bodyTypeLabel),
        bodyShopName: pdf(bodyShopName),
        bodyShopKvps: pdf(bodyShopKvpsLabel),
        shippedToBodyShop: pdf(shippedToBodyShopLabel),
      },
      client: {
        isRentSale,
        isDirectorSale,
        clientType,
        companySiret: pdf(
          isDirectorSale ? displayValue(currentDraft?.siretInput) : undefined,
        ),
        companyDenomination: pdf(
          isDirectorSale
            ? displayValue(currentDraft?.companyDenomination)
            : undefined,
        ),
        lastName: pdf(
          individualClient
            ? displayValue(currentDraft?.clientLastName)
            : companyClient
              ? displayValue(currentDraft?.companyDenomination)
              : undefined,
        ),
        firstName: pdf(
          individualClient
            ? displayValue(currentDraft?.clientFirstName)
            : undefined,
        ),
        address: pdf(
          individualClient
            ? displayValue(currentDraft?.clientAddressLine)
            : displayValue(currentDraft?.companyAddressLine),
        ),
        postalCode: pdf(
          individualClient
            ? displayValue(currentDraft?.clientPostalCode)
            : displayValue(currentDraft?.companyPostalCode),
        ),
        city: pdf(
          individualClient
            ? displayValue(currentDraft?.clientCity)
            : displayValue(currentDraft?.companyCity),
        ),
        email: pdf(
          individualClient
            ? displayValue(currentDraft?.clientEmail)
            : displayValue(currentDraft?.companyEmail),
        ),
        mobilePhone: pdf(
          individualClient
            ? displayValue(currentDraft?.clientMobilePhone)
            : displayValue(currentDraft?.companyMobilePhone),
        ),
        directorSale: pdf(directorSaleLabel),
      },
      funding: {
        isLeased: formatRetailCtmPdfYesNo(currentDraft?.isLeased),
        isVwfsLeasing: formatRetailCtmPdfYesNo(isVwfsLeasing),
        leasingType: pdf(leasingTypeLabel),
        lessorSiret: pdf(lessorSiretLabel),
        lessorBdcNumber: pdf(displayValue(currentDraft?.businessNumber)),
        saleChannel: pdf(saleTypeCode),
        hasTradeIn: formatRetailCtmPdfTradeIn(hasTradeIn, tradeInCount),
      },
      additional: {
        salesPerson: pdf(salesPersonLabel),
        consent: formatRetailCtmPdfYesNo(currentDraft?.vgfOffersConsent),
        preferredProductionWeek: pdf(vehicleProductionDate),
        preferredDeliveryDate: formatRetailCtmPdfDate(
          currentDraft?.preferredDeliveryDate,
        ),
        maxDeliveryDate: formatRetailCtmPdfDate(currentDraft?.maxDeliveryDate),
        signatureDate: formatRetailCtmPdfDate(currentDraft?.signatureDate),
        tradeInLines: tradeInPdfLines,
      },
      productionNumber: vehicleProductionNumber,
      productionYear: vehicleProductionYear,
    };
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      await downloadRetailCtmSummaryPdf(buildPdfData());
    } catch {
      toast.error("Impossible de générer le PDF du récapitulatif.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleValidate = async () => {
    if (!currentDraft?.id) {
      toast.error("Aucun brouillon associé à cette affectation.");
      return;
    }

    if (mdmClientHealthMessage) {
      showStepError(mdmClientHealthMessage);
      return;
    }

    setIsSubmittingCtm(true);
    refPersSubmitAttemptedRef.current = true;
    setRefPersTransmission({ status: "in_progress" });
    setCtmTransmission({ status: "pending" });

    try {
      const result = await syncMdmFromDraft({ draftId: currentDraft.id });
      if (!result.success) {
        setRefPersTransmission({
          status: "failed",
          message: result.message,
        });
        toast.error(
          result.message ??
            "La synchronisation RefPers a échoué. La transmission CTM est bloquée.",
        );
        return;
      }

      const draftUpdates: Partial<typeof currentDraft> = {};
      if (result.clientId && result.clientId !== currentDraft.clientId) {
        draftUpdates.clientId = result.clientId;
      }
      if (
        result.companyMdmSourceId &&
        result.companyMdmSourceId !== currentDraft.companyMdmSourceId
      ) {
        draftUpdates.companyMdmSourceId = result.companyMdmSourceId;
        draftUpdates.companyRegisteredInMdm = true;
      }
      if (Object.keys(draftUpdates).length > 0) {
        setCurrentDraft({ ...currentDraft, ...draftUpdates });
      }

      setRefPersTransmission({
        status:
          result.mdmOutcome === "not_applicable" ||
          result.mdmOutcome === "skipped"
            ? "skipped"
            : "success",
        mdmOutcome: result.mdmOutcome,
      });

      setCtmTransmission({ status: "in_progress" });
      const ctmResult = await transmitRetailCtmAction({
        draftId: currentDraft.id,
      });

      if (!ctmResult.success) {
        setCtmTransmission({
          status: "failed",
          message: ctmResult.message,
        });
        toast.error(
          ctmResult.message ??
            "La transmission CTM a échoué. Réessayez ou contactez le support technique.",
        );
        return;
      }

      setCtmTransmission({
        status: "success",
      });
      resetVehicleAssignmentStores();
      toast.success("CTM transmise avec succès.");
      router.push("/backoffice/cat/vehicle-assignment");
    } catch {
      setRefPersTransmission({
        status: "failed",
        message:
          "La synchronisation RefPers a échoué. La transmission CTM est bloquée.",
      });
      toast.error(
        "La synchronisation RefPers a échoué. La transmission CTM est bloquée.",
      );
    } finally {
      setIsSubmittingCtm(false);
    }
  };

  const submitButtonLabel = isSubmittingCtm
    ? refPersTransmission.status === "in_progress" ||
      (refPersTransmission.status === "pending" && !refPersTransmission.message)
      ? "Synchronisation RefPers…"
      : "Transmission CTM…"
    : "Transmettre CTM";

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-semibold">
          <CircleCheckBigIcon className="text-primary size-6 shrink-0" />
          Récapitulatif & Validation CTM
        </h2>
      </div>

      <Alert className="border-green-200 bg-green-50 text-green-900 [&>svg]:text-green-700">
        <CircleCheckBigIcon aria-hidden />
        <AlertTitle className="font-semibold text-green-900">
          Prêt pour Validation
        </AlertTitle>
        <AlertDescription className="text-green-800">
          Veuillez vérifier toutes les informations ci-dessous avant de valider
          l&apos;attribution. Une fois validée, la CTM sera transmise via
          Mulesoft.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <SummaryInfoCard
          title="Caractéristiques du Dossier"
          icon={FileTextIcon}
        >
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-[#005EA8] hover:bg-[#005EA8]/90">
              Cas Retail
            </Badge>
            <Badge
              variant="outline"
              className="border-[#005EA8] bg-white text-[#005EA8] hover:bg-white"
            >
              KVPS: {vehicleKvps}
            </Badge>
            {hasTradeIn ? (
              <Badge className="bg-[#005EA8] hover:bg-[#005EA8]/90">
                Avec reprise ({tradeInCount})
              </Badge>
            ) : (
              <Badge variant="outline">Sans reprise</Badge>
            )}
          </div>
        </SummaryInfoCard>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SummaryInfoCard title="Informations Véhicule" icon={CarIcon}>
          <div className="space-y-2">
            <p className="text-sm font-semibold">Détails du Véhicule</p>
            <SummaryFieldRow label="VIN">
              <span className="text-primary underline-offset-2">
                {vehicleVin}
              </span>
            </SummaryFieldRow>
            <SummaryFieldRow label="Numéro de commande usine">
              {vehicleCommissionNumber}
            </SummaryFieldRow>
            <SummaryFieldRow label="Année de commande usine">
              {vehicleCommissionYear}
            </SummaryFieldRow>
            <SummaryFieldRow label="Marque">
              <Badge className="bg-[#005EA8] hover:bg-[#005EA8]/90">
                {vehicleBrand}
              </Badge>
            </SummaryFieldRow>
            <SummaryFieldRow label="Code modèle">
              {vehicleModelCode}
            </SummaryFieldRow>
            <SummaryFieldRow label="Libellé modèle">
              {vehicleModelDescription}
            </SummaryFieldRow>
            <SummaryFieldRow label="Année modèle">
              {vehicleModelYear}
            </SummaryFieldRow>
            <SummaryFieldRow label="Code peinture">
              {vehiclePaintCode}
            </SummaryFieldRow>
            <SummaryFieldRow label="Libellé peinture">
              {vehiclePaintDescription}
            </SummaryFieldRow>
            <SummaryFieldRow label="Code intérieur">
              {vehicleInteriorCode}
            </SummaryFieldRow>
            <SummaryFieldRow label="Libellé intérieur">
              {vehicleInteriorDescription}
            </SummaryFieldRow>
            <SummaryFieldRow label="Options">
              <span className="wrap-break-words block max-w-[280px] text-right">
                {vehicleOptions}
              </span>
            </SummaryFieldRow>
            <p className="pt-2 text-sm font-semibold">Statut & Production</p>
            <SummaryFieldRow label="Production">
              {vehicleProductionDate}
            </SummaryFieldRow>
          </div>
        </SummaryInfoCard>

        {showClientRecap ? (
          <SummaryInfoCard title="Informations client" icon={UserIcon}>
            <div className="space-y-6">
              <SummaryFieldRow label="Type de vente client">
                {clientRecapTypeLabel}
              </SummaryFieldRow>

              {showCompanyRecap ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Société</p>
                  <RecapFieldsList fields={companyRecapFields} />
                </div>
              ) : null}

              {showIndividualRecap ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold">
                    {individualRecapSectionTitle}
                  </p>
                  <RecapFieldsList fields={individualRecapFields} />
                </div>
              ) : null}
            </div>
          </SummaryInfoCard>
        ) : null}

        <SummaryInfoCard title="Financement" icon={CreditCardIcon}>
          <div className="space-y-2">
            <SummaryFieldRow label="Vente à dirigeant">
              {directorSaleLabel}
            </SummaryFieldRow>
            <SummaryFieldRow label="Location VWFS">
              {formatBooleanYesNo(isVwfsLeasing)}
            </SummaryFieldRow>
            <SummaryFieldRow label="Type de location">
              {leasingTypeLabel}
            </SummaryFieldRow>
            <SummaryFieldRow label="Type de vente">
              <Badge className="bg-[#005EA8] hover:bg-[#005EA8]/90">
                {saleTypeCode}
              </Badge>
            </SummaryFieldRow>
            {isVehicleLeased ? (
              <>
                <SummaryFieldRow label="SIRET du loueur">
                  {lessorSiretLabel}
                </SummaryFieldRow>
                <p className="pt-2 text-sm font-semibold">Conducteur</p>
                <SummaryFieldRow label="Prénom">
                  {driverFirstName}
                </SummaryFieldRow>
                <SummaryFieldRow label="Nom">{driverLastName}</SummaryFieldRow>
              </>
            ) : null}
          </div>
        </SummaryInfoCard>

        <SummaryInfoCard
          title={`Reprise${hasTradeIn ? ` (${tradeInCount})` : ""}`}
          icon={ArrowLeftRightIcon}
        >
          {hasTradeIn ? (
            <ul className="space-y-2 text-sm">
              {tradeInEntries.map((entry) => (
                <li key={entry.key} className="font-medium">
                  {entry.label}: {entry.value}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">
              Aucun véhicule de reprise
            </p>
          )}
        </SummaryInfoCard>

        <SummaryInfoCard
          title="Informations Complémentaires"
          icon={FileTextIcon}
        >
          <div className="space-y-2">
            <SummaryFieldRow label="Date de signature du BC">
              {signatureDateLabel}
            </SummaryFieldRow>
            <SummaryFieldRow label="Date de livraison souhaitée">
              {preferredDeliveryLabel}
            </SummaryFieldRow>
            <SummaryFieldRow label="Date livraison extrême">
              {maxDeliveryDateLabel}
            </SummaryFieldRow>
            <SummaryFieldRow label="Vendeur (LEMA)">
              {salesPersonLabel}
            </SummaryFieldRow>
          </div>
        </SummaryInfoCard>
      </div>

      <RetailCtmTransmissionTaskList
        key={transmissionListKey}
        tasks={transmissionTasks}
        title="Statut de la transmission CTM"
        titleIcon={SendIcon}
      />

      <FlexRow className={cn("border-border border-t pt-4")}>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (!onGoToPreviousStep) {
              return;
            }
            onGoToPreviousStep(Math.max(step - 1, 1));
          }}
        >
          <ArrowLeftIcon aria-hidden />
          Précédent
        </Button>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
          >
            <DownloadIcon aria-hidden />
            {isGeneratingPdf ? "Génération du PDF…" : "Télécharger PDF"}
          </Button>
          <Button
            type="button"
            onClick={() => void handleValidate()}
            disabled={
              isGeneratingPdf ||
              isSubmittingCtm ||
              Boolean(mdmClientHealthMessage)
            }
          >
            <SendIcon aria-hidden />
            {isSubmittingCtm ? submitButtonLabel : "Transmettre CTM"}
          </Button>
        </div>
      </FlexRow>
    </div>
  );
}
